import { useTranslation } from 'react-i18next';
import { MEMBERSHIP_MODELS } from '@/mocks/dealer';
import type { ApplicationType, MembershipTier } from '@/types/domain';

const ICONS: Record<MembershipTier, string> = {
  standard: 'ri-user-line',
  a: 'ri-vip-crown-line',
  b: 'ri-medal-line',
  c: 'ri-award-line',
};

interface MembershipModelsSectionProps {
  onApply: (type: ApplicationType | '') => void;
}

export default function MembershipModelsSection({ onApply }: MembershipModelsSectionProps) {
  const { t } = useTranslation();

  return (
    <section className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-3xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-4xl lg:text-5xl">
            {t('dealer.modelsTitle')}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-foreground-600 md:text-lg">
            {t('dealer.modelsSubtitle')}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {MEMBERSHIP_MODELS.map((model, index) => (
            <div
              key={model.id}
              className={`reveal flex flex-col rounded-lg border bg-background-50 p-6 transition-colors ${
                model.highlight
                  ? 'border-primary-400'
                  : 'border-background-200 hover:border-primary-300'
              } ${index === 1 ? 'reveal-delay-1' : index === 2 ? 'reveal-delay-2' : index === 3 ? 'reveal-delay-3' : ''}`}
            >
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-md ${
                  model.highlight ? 'bg-primary-500 text-foreground-950' : 'bg-secondary-100 text-secondary-900'
                }`}
              >
                <i className={`${ICONS[model.id]} text-2xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-5 font-heading text-xl text-foreground-950">
                {t(model.labelKey)}
              </h3>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">
                {t(model.descKey)}
              </p>
              <button
                type="button"
                onClick={() => onApply(model.applicationType ?? '')}
                className={`mt-6 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer ${
                  model.highlight
                    ? 'bg-primary-500 text-foreground-950 hover:bg-primary-400'
                    : 'border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                {t('dealer.cardCta')}
                <i className="ri-arrow-right-line rtl:rotate-180" aria-hidden="true"></i>
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}